<script lang="ts" setup>
import {onBeforeUnmount, onMounted, ref} from 'vue'
import {useRouter} from 'vue-router'
import YonergeService from '@/services/yonerge'
import YarismaService from '@/services/yarisma'
import type {IYarisma} from '@/model/yarisma'
import {useKalanSureler} from '@/composables/useKalanSureler'
import Header from '@/components/home/Header.vue'
import YarismaSonaErdi from '@/components/home/YarismaSonaErdi.vue'
import GeriSayim from '@/components/home/GeriSayim.vue'
import SonucYayinlandi from '@/components/home/SonucYayinlandi.vue'
import YarismaInfo from '@/components/home/YarismaInfo.vue'
import {ErrorToast} from '@/utils/popup'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import LogService, {LogIslem, LogSayfa} from "@/services/log.ts";
import {UAParser} from "ua-parser-js";
import ChromeInfo from "@/components/home/ChromeInfo.vue";
import {TimerService} from '@/services/timer'
import SartnameDurumu from "@/components/home/SartnameDurumu.vue";
import { useI18n } from 'vue-i18n'
const router = useRouter()

const {
  baslamasinaKalanSure,
  bitmesineKalanSure,
  yarismaKalansure,
  getSunucuKalanSure,
  sunucuKalanSureTimerBaslat,
  baslamasinaGeriSayimBaslat,
  baslamasinaGeriSayiminiDurdur,
} = useKalanSureler()

const yonergeOnaylandiMi = ref(false)
const yonergeKartiAktifMi = ref(false)
const yarisma = ref<IYarisma | null>(null)
const yarismaBittiMi = ref(false)
const loading = ref(true)
const isChrome = ref(false)
let refreshTimer: TimerService | undefined
const { t } = useI18n()

onMounted(async () => {
  const browser = new UAParser().getBrowser()
  isChrome.value = browser.name?.toLowerCase().includes('chrome') ?? false
  LogService.log(LogSayfa.ANASAYFA, LogIslem.ANASAYFA_GORUNTULEME, {
    browser: browser.name,
    isChrome: isChrome.value
  })
  loading.value = true

  // 1. yarışma bilgilerini al
  const [err, yarismaData] = await YarismaService.getYarismaBilgisi()

  if (err) {
    loading.value = false
    ErrorToast(t('error_page.error_occurred'))
    await router.push({
      path: '/error',
      query: {
        status: '500',
        message: t('error_page.error_try_again') + ' ' + err,
        force: 'true',
      },
    })
    return
  }
  yarisma.value = yarismaData
  if (yarisma.value.sonuc_aciklandi_mi) {
    return
  }

  // 2. yarışma bitti mi kontrol et. Local storage' kontrolü
  if (YarismaService.getYarismaBittiMi()) {
    yarismaBittiMi.value = true
    loading.value = false
    return
  }

  // 3. sunucudan kalan süreyi al. Yarışmacı sunucuda bitirmiş olabilir, veya yarışma bitiş süresi geçmiş olabilir.
  const [errKalansure, bittiMi] = await getSunucuKalanSure()
  if (errKalansure) {
    loading.value = false
    return
  }
  if (bittiMi) {
    yarismaBittiMi.value = true
    loading.value = false
    return
  }
  YarismaService.setBaslamasinaKalanSure(baslamasinaKalanSure.value)
  YarismaService.setBitmesineKalanSure(bitmesineKalanSure.value)
  yonergeOnaylandiMi.value = YonergeService.onaylandiMi()
  yonergeKartiAktifMi.value = !yonergeOnaylandiMi.value && baslamasinaKalanSure.value < 60 * 30

  // 4. Yarışma başlamadan 15 dakika önce yönerge sayfasına yönlendir.
  if (baslamasinaKalanSure.value < 60 * 15 && !yonergeOnaylandiMi.value) {
    LogService.log(LogSayfa.ANASAYFA, LogIslem.YONERGE_REDIRECT)
    return await router.push('yonerge')
  }

  // 5. yarışma başlamışsa hiç geri sayıma gelme
  if (baslamasinaKalanSure.value <= 0) {
    return await router.push('/yarisma?late=true')
  }

  // 6. yarışma başlamasına 30 dakikadan fazla varsa sayfayı 10 dakikada bir yenile. Sonraki aşamada geri sayım başlayacak.
  if (baslamasinaKalanSure.value > 60 * 30) {
    let timeout = 60 * 10  // 10 dakika = 600 saniye
    if (baslamasinaKalanSure.value < 60 * 40) {
      // 30-40 dk arasındaysa, tam 30 dk kalınca yenilensin
      timeout = baslamasinaKalanSure.value - 60 * 30
    }
    refreshTimer = new TimerService(ref(timeout), null, () => {
      location.reload()
    })
    refreshTimer.start()
    loading.value = false
    return
  }

  // 7. kalan süre sayacını başlat
  sunucuKalanSureTimerBaslat()
  baslamasinaGeriSayimBaslat()
  loading.value = false
})

onBeforeUnmount(() => {
  baslamasinaGeriSayiminiDurdur()
  refreshTimer?.stop()
})
</script>

<template>
  <div
    class="flex flex-col items-center justify-start min-h-screen w-full mt-10 md:mt-20 px-4 md:px-8 max-w-[860px] mx-auto gap-4"
  >
    <LoadingSpinner v-if="loading" />
    <Header v-if="!loading" />

    <SonucYayinlandi
      v-if="yarisma?.sonuc_aciklandi_mi"
      v-show="!loading"
      :siralamaAciklandiMi="yarisma?.siralama_aciklandi_mi"
      @update:loading="loading = $event"
    />

    <template v-if="!loading && !yarisma?.sonuc_aciklandi_mi">
      <ChromeInfo v-if="!isChrome"/>
      <YarismaSonaErdi v-if="yarismaBittiMi" :bitisMetni="yarisma?.bitis_metni" />
      <template v-else>
        <YarismaInfo
          :yarisma="yarisma"
          :yarismaKalansure="yarismaKalansure"
          :baslamasinaKalanSure="baslamasinaKalanSure"
        />
        <GeriSayim
          v-if="baslamasinaKalanSure < 1800"
          :baslamasina-kalan-sure="baslamasinaKalanSure"
          :yonerge-onaylandi-mi="yonergeOnaylandiMi"
        />
        <SartnameDurumu v-if="yonergeKartiAktifMi" :yonerge-onaylandi-mi="yonergeOnaylandiMi"/>
      </template>
    </template>
  </div>
</template>
